import * as Collapsible from '@radix-ui/react-collapsible';
import { FontFamily, Link, LinkSize, LinkType, Text, TextColor, TextSize, TextVariant } from '@vizit/atoms';
import { COLLECTION_SCORING_ACCURACY_HELP_URL } from '@vizit/constants';
import { ChevronDownSmIcon } from '@vizit/icons';
import { AdvisoryBanner } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { HeroScoringRow } from './hero-scoring-row';
import { StatusBadge } from './status-badge';
import type { HeroScoringNotice, HeroScoringSection } from './types';
import { useSectionOpenState } from './use-section-open-state';

function SectionNotice({ notice }: { notice: HeroScoringNotice }) {
  if (notice !== 'collection-accuracy') return null;

  return (
    <AdvisoryBanner
      title="Collection scores are less accurate than PDP scores"
      description="Without live product-page context, Shopper Clarity checks are estimated and may shift once this hero is scored on a PDP."
      action={
        COLLECTION_SCORING_ACCURACY_HELP_URL === null ? undefined : (
          <Link
            href={COLLECTION_SCORING_ACCURACY_HELP_URL}
            target="_blank"
            rel="noreferrer"
            type={LinkType.External}
            size={LinkSize.Sm}
          >
            Learn more
          </Link>
        )
      }
    />
  );
}

interface HeroScoringRowsSectionProps {
  section: HeroScoringSection;
}

function HeroScoringRowsSection({ section }: HeroScoringRowsSectionProps) {
  const { id, title, description, status, defaultOpen = false, body, notice } = section;
  const [open, setOpen] = useSectionOpenState(id, defaultOpen);

  if (body.kind !== 'rows') return null;

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="flex flex-col gap-3">
      <Collapsible.Trigger
        className={cx(
          'group flex w-full items-center gap-3 pr-6',
          'cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        )}
      >
        <ChevronDownSmIcon
          size={11}
          className={cx(
            'shrink-0 text-gray-utility-400 transition-all duration-200 group-hover:text-gray-utility-500',
            '-rotate-90 group-data-[state=open]:rotate-0',
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
          <Text fontFamily={FontFamily.Serif} size={TextSize.Xl} elementType="span" showEllipsis={false}>
            {title}
          </Text>
          {description !== undefined && (
            <Text variant={TextVariant.XsRegular} color={TextColor.Quaternary} showEllipsis={false} elementType="p">
              {description}
            </Text>
          )}
        </div>
        {status !== undefined && <StatusBadge status={status} className="self-start" />}
      </Collapsible.Trigger>
      {/* Outside the collapsible on purpose: the open/closed choice is persisted across hero pages,
          so nesting the caveat would let one collapse hide it for good. */}
      {notice !== undefined && <SectionNotice notice={notice} />}
      <Collapsible.Content className="data-[state=closed]:hidden">
        {body.rows.length > 0 && (
          <div className="flex flex-col gap-6 bg-secondary-alt p-6">
            {body.rows.map((row) => (
              <HeroScoringRow key={row.label} label={row.label} status={row.status} />
            ))}
          </div>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

export { HeroScoringRowsSection };
export type { HeroScoringRowsSectionProps };
